/**
 * ExpertModeContext — Shared state for expert mode toggle.
 *
 * Without a context, each useExpertMode() call creates its own independent
 * useState. Toggling in one component wouldn't affect others. The context
 * ensures ALL components see the same state and switch instantly.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'expertMode';

interface ExpertModeContextValue {
  expertMode: boolean;
  toggle: () => void;
}

const ExpertModeContext = createContext<ExpertModeContextValue>({
  expertMode: false,
  toggle: () => {},
});

export function ExpertModeProvider({ children }: { children: React.ReactNode }) {
  const [expertMode, setExpertMode] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    setExpertMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch { /* ignore */ }
      return next;
    });
  }, []);

  return (
    <ExpertModeContext.Provider value={{ expertMode, toggle }}>
      {children}
    </ExpertModeContext.Provider>
  );
}

export function useExpertMode(): ExpertModeContextValue {
  return useContext(ExpertModeContext);
}
